import { useState, useEffect, useCallback } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListChecks, Plus, Pencil, Trash2, Send, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const TARGET_OPTS = [
  { key: "visitor", label: "Visitor / guest" },
  { key: "submitter", label: "Person who made the booking" },
  { key: "dept_head", label: "Dept head approver (the one who approved this request)" },
  { key: "cmo", label: "CMO / Admin approver" },
  { key: "housing", label: "Housing approver / coordinator" },
];

const EMPTY = { name: "", url: "", recipients: ["visitor"], extra_emails: [], owner_user_ids: [], reminder_enabled: true, reminder_days: 3 };

export default function Surveys() {
  const [surveys, setSurveys] = useState([]);
  const [sends, setSends] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [extraDraft, setExtraDraft] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, a, u] = await Promise.all([
        api.get("/surveys"),
        api.get("/survey-sends?scope=all").catch(() => ({ data: [] })),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setSurveys(s.data || []); setSends(a.data || []); setUsers(u.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load surveys"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (field, val) => setEditing((p) => ({
    ...p, [field]: p[field].includes(val) ? p[field].filter((x) => x !== val) : [...p[field], val],
  }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/surveys/${editing.id}`, editing);
      else await api.post("/surveys", editing);
      toast.success("Survey saved");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete survey "${s.name}"?`)) return;
    try { await api.delete(`/surveys/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const runNow = async () => {
    try {
      const { data } = await api.post("/surveys/run-dispatch");
      toast.success(`Dispatched ${data.dispatched}, reminders ${data.reminders}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Dispatch failed"); }
  };

  const complete = async (id) => {
    try { await api.post(`/survey-sends/${id}/complete`); toast.success("Marked complete"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const userName = (id) => (users.find((u) => u.id === id) || {}).name || id;
  const sendStatus = (s) => s.completed_at
    ? { label: "Completed", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" }
    : s.accessed_at
      ? { label: "Link opened", cls: "bg-blue-50 text-blue-800 border-blue-200" }
      : { label: `Awaiting (${s.reminder_count || 0} reminders)`, cls: "bg-amber-100 text-amber-900 border-amber-300" };

  return (
    <div className="space-y-6 fade-in" data-testid="surveys-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope, sans-serif" }}>
            <ListChecks className="h-8 w-8 text-[#40916C]" /> Post-stay surveys
          </h1>
          <p className="text-[#525C58] mt-1 max-w-2xl">
            Surveys are emailed automatically once a stay ends, with reminders until the link is
            opened or an owner marks the cycle complete. Attach them to bookings and guest requests.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runNow} className="border-[#1B4332]/20" data-testid="survey-run-dispatch-btn">
            <Send className="h-4 w-4 mr-1" /> Run dispatch now
          </Button>
          <Button onClick={() => setEditing({ ...EMPTY })} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="add-survey-btn">
            <Plus className="h-4 w-4 mr-1" /> New survey
          </Button>
        </div>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader><CardTitle className="text-base">{surveys.length} survey{surveys.length === 1 ? "" : "s"}</CardTitle></CardHeader>
        <CardContent>
          {surveys.length === 0 ? (
            <p className="text-[#7A8A84] text-center py-8" data-testid="surveys-empty">No surveys yet — create one, then tick it on a booking.</p>
          ) : (
            <Table data-testid="surveys-table">
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Sending pattern</TableHead>
                <TableHead>Owner(s)</TableHead><TableHead>Reminders</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {surveys.map((s) => (
                  <TableRow key={s.id} data-testid={`survey-row-${s.id}`}>
                    <TableCell className="font-medium">
                      <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#40916C]">
                        {s.name} <ExternalLink className="h-3 w-3" />
                      </a>
                    </TableCell>
                    <TableCell><div className="flex flex-wrap gap-1">
                      {(s.recipients || []).map((r) => <Badge key={r} variant="outline" className="text-[10px] bg-[#F0F5F2] border-[#1B4332]/15 text-[#1B4332]">{(TARGET_OPTS.find((t) => t.key === r) || {}).label || r}</Badge>)}
                      {(s.extra_emails || []).map((e) => <Badge key={e} variant="outline" className="text-[10px]">{e}</Badge>)}
                    </div></TableCell>
                    <TableCell className="text-xs text-[#525C58]">{(s.owner_user_ids || []).map(userName).join(", ") || "—"}</TableCell>
                    <TableCell className="text-xs">{s.reminder_enabled ? `Every ${s.reminder_days} day(s)` : "Off"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ ...EMPTY, ...s })} data-testid={`edit-survey-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(s)} data-testid={`delete-survey-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#E8E8E6]">
        <CardHeader><CardTitle className="text-base">Survey activity ({sends.length})</CardTitle></CardHeader>
        <CardContent>
          {sends.length === 0 ? (
            <p className="text-[#7A8A84] text-center py-6">Nothing sent yet — surveys go out automatically when a tagged stay ends.</p>
          ) : (
            <Table data-testid="survey-activity-table">
              <TableHeader><TableRow>
                <TableHead>Visitor</TableHead><TableHead>Survey</TableHead><TableHead>Sent to</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {sends.map((s) => {
                  const st = sendStatus(s);
                  return (
                    <TableRow key={s.id} data-testid={`send-row-${s.id}`}>
                      <TableCell className="font-medium">{s.visitor_name}<div className="text-[10px] text-[#7A8A84]">stay ended {s.stay_end}</div></TableCell>
                      <TableCell className="text-xs">{s.survey_name}</TableCell>
                      <TableCell className="text-[11px] text-[#525C58]">{(s.recipients || []).join(", ")}</TableCell>
                      <TableCell><Badge className={`${st.cls} border text-[10px]`}>{st.label}</Badge>
                        {s.completed_by_name && <div className="text-[10px] text-[#7A8A84] mt-0.5">by {s.completed_by_name}</div>}
                      </TableCell>
                      <TableCell className="text-right">
                        {!s.completed_at && (
                          <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-800 h-7 text-xs"
                                  onClick={() => complete(s.id)} data-testid={`complete-send-${s.id}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Complete
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit survey" : "New survey"}</DialogTitle>
            <DialogDescription>Sent automatically once a tagged stay concludes.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-1"><Label className="text-xs">Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Post-visit feedback" data-testid="survey-name-input" /></div>
              <div className="space-y-1"><Label className="text-xs">Survey link (Google Forms, SurveyMonkey…)</Label>
                <Input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder="https://…" data-testid="survey-url-input" /></div>
              <div className="space-y-1"><Label className="text-xs">Send to (per booking)</Label>
                <div className="flex flex-col gap-1 border border-[#E8E8E6] rounded-lg p-2">
                  {TARGET_OPTS.map((t) => (
                    <label key={t.key} className="flex items-center gap-2 text-sm cursor-pointer px-1 rounded hover:bg-[#F4F4F1]">
                      <input type="checkbox" checked={editing.recipients.includes(t.key)} onChange={() => toggle("recipients", t.key)} data-testid={`survey-target-${t.key}`} /> {t.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Also always send to (fixed emails)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {editing.extra_emails.map((em) => (
                    <span key={em} className="inline-flex items-center gap-1 bg-[#F4F4F1] border border-[#E8E8E6] rounded-full px-2 py-0.5 text-xs">
                      {em}<button type="button" className="text-red-600" onClick={() => toggle("extra_emails", em)}>×</button></span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={extraDraft} onChange={(e) => setExtraDraft(e.target.value)} placeholder="email@example.com" className="flex-1"
                         onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const v = extraDraft.trim(); if (v.includes("@") && !editing.extra_emails.includes(v)) setEditing({ ...editing, extra_emails: [...editing.extra_emails, v] }); setExtraDraft(""); } }}
                         data-testid="survey-extra-email-input" />
                  <Button type="button" size="sm" variant="outline" onClick={() => { const v = extraDraft.trim(); if (v.includes("@") && !editing.extra_emails.includes(v)) setEditing({ ...editing, extra_emails: [...editing.extra_emails, v] }); setExtraDraft(""); }}>Add</Button>
                </div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Owner(s) — must mark each cycle complete</Label>
                <div className="max-h-36 overflow-y-auto border border-[#E8E8E6] rounded-lg p-2 grid grid-cols-2 gap-1">
                  {users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-xs cursor-pointer px-1 rounded hover:bg-[#F4F4F1]">
                      <input type="checkbox" checked={editing.owner_user_ids.includes(u.id)} onChange={() => toggle("owner_user_ids", u.id)} data-testid={`survey-owner-${u.id}`} /> {u.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={editing.reminder_enabled} onChange={(e) => setEditing({ ...editing, reminder_enabled: e.target.checked })} data-testid="survey-reminder-toggle" /> Send reminders every
                </label>
                <Input type="number" min={1} max={60} value={editing.reminder_days} disabled={!editing.reminder_enabled}
                       onChange={(e) => setEditing({ ...editing, reminder_days: Number(e.target.value) })} className="w-16 h-8" data-testid="survey-reminder-days" />
                <span className="text-sm text-[#525C58]">day(s) until opened or completed</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="survey-save-btn">{saving ? "Saving…" : "Save survey"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
